import { printBrandColor } from './branding';
/**
 * Two ways a screen hands a table to the outside: a CSV that Excel opens
 * as-is (semicolons and a BOM, which is what Excel in a Romanian locale
 * expects), and a print window the browser saves as a PDF.
 */

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",;\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
}

export function downloadCsv(filename: string, rows: unknown[][]): void {
  const body = rows.map((r) => r.map(csvCell).join(';')).join('\r\n');
  const blob = new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

const esc = (v: unknown) =>
  String(v ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');

/**
 * A print window: writes the page, wires its buttons, and asks for printing.
 *
 * The blank window inherits the application's Content-Security-Policy, which
 * runs no inline code. In production an `onclick="window.print()"` or a
 * `<script>` in the page does nothing: the "Printează" button in the window
 * was dead, and the quote, the prescription and the receipt — which printed
 * themselves from a `<script>` — never printed at all. Development has no CSP,
 * so all of it worked there. The page therefore carries attributes, and the
 * wiring happens from here, in the application's own window: `data-print`
 * prints, `data-close` closes, and an image marked `data-drop-on-error` is
 * removed when it fails to load.
 *
 * Printing starts once the page has loaded, so a logo makes it onto paper;
 * after three seconds it starts regardless. Returns the window, or null when
 * the browser blocked it.
 */
export function openPrintWindow(
  html: string,
  {
    width = 900,
    height = 1000,
    delay = 400,
  }: { width?: number; height?: number; delay?: number } = {},
): Window | null {
  const w = window.open('', '_blank', `width=${width},height=${height}`);
  if (!w) return null;
  w.document.write(html);
  w.document.close();
  wirePrintWindow(w);

  let asked = false;
  const print = () => {
    if (asked) return;
    asked = true;
    setTimeout(() => {
      try {
        w.print();
      } catch {
        /* the user can print from the window */
      }
    }, delay);
  };
  if (w.document.readyState === 'complete') print();
  else w.addEventListener('load', print, { once: true });
  setTimeout(print, 3000);
  return w;
}

/** The buttons and images of a print window — see `openPrintWindow`. */
export function wirePrintWindow(w: Window): void {
  const doc = w.document;
  doc.querySelectorAll<HTMLElement>('[data-print]').forEach((el) => {
    el.addEventListener('click', () => w.print());
  });
  doc.querySelectorAll<HTMLElement>('[data-close]').forEach((el) => {
    el.addEventListener('click', () => w.close());
  });
  doc.querySelectorAll<HTMLImageElement>('img[data-drop-on-error]').forEach((img) => {
    img.addEventListener('error', () => img.remove());
    // An image that failed before this ran fires no further `error`; `decode`
    // reports it. (Not `naturalWidth === 0`: an SVG logo has none either.)
    if (img.complete && typeof img.decode === 'function') {
      img.decode().catch(() => img.remove());
    }
  });
}

/**
 * Opens a printable page — clinic name, title, a line of context, the table —
 * and asks the browser to print it. Returns false when the pop-up was
 * blocked, so the caller can say so.
 */
/** Un tabel al aceleiași hârtii: propriul antet, propriile capete de coloană. */
export interface PrintSection {
  /** Titlul secțiunii, deasupra tabelului. */
  subtitle?: string;
  headers: string[];
  rows: unknown[][];
}

const renderTable = (headers: string[], rows: unknown[][]): string => {
  const thead = `<tr>${headers.map((h) => `<th>${esc(h)}</th>`).join('')}</tr>`;
  const tbody = rows.map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`).join('');
  return `<table><thead>${thead}</thead><tbody>${tbody}</tbody></table>`;
};

export function printTable(opts: {
  title: string;
  clinicName: string;
  meta?: string;
  headers: string[];
  rows: unknown[][];
  /** Tabele suplimentare, fiecare cu titlul și capetele lui — sub cel principal. */
  sections?: PrintSection[];
}): boolean {
  const brand = printBrandColor();
  const main = renderTable(opts.headers, opts.rows);
  const extra = (opts.sections ?? [])
    .map(
      (s) =>
        `${s.subtitle ? `<h3 class="sub">${esc(s.subtitle)}</h3>` : ''}${renderTable(s.headers, s.rows)}`,
    )
    .join('');
  const w = openPrintWindow(
    `<!doctype html><html><head><meta charset="utf-8"><title>${esc(opts.title)}</title>
    <style>@page{margin:16mm}body{font-family:Arial,sans-serif;color:#0a0f1e;font-size:12px}
    .head{display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid ${brand};padding-bottom:8px;margin-bottom:6px}
    .brand{font-size:20px;font-weight:800;color:${brand}}.clinic{font-size:11px;color:#666}
    .meta{font-size:11px;color:#666;margin-bottom:14px}
    .sub{font-size:12.5px;font-weight:800;margin:20px 0 6px}
    table{width:100%;border-collapse:collapse;font-size:11.5px}
    th{background:${brand};color:#fff;text-align:left;padding:7px 9px;font-weight:700}
    td{padding:6px 9px;border-bottom:1px solid #e5e7eb}
    tr:nth-child(even) td{background:#f8fafc}
    @media print{.noprint{display:none}}</style></head>
    <body><div class="head"><div class="brand">${esc(opts.clinicName)}</div><div class="clinic">${esc(opts.title)}</div></div>
    <div class="meta">${esc(opts.meta ?? '')}</div>
    ${main}${extra}
    <div class="noprint" style="margin-top:22px;text-align:center"><button data-print style="padding:10px 22px;font-size:14px;cursor:pointer">Printează / Salvează PDF</button></div>
    </body></html>`,
    { width: 900, height: 950 },
  );
  return w !== null;
}

/**
 * O hârtie, nu un tabel: cerere de concediu, fișă de evaluare, proces-verbal.
 *
 * Aceleași margini și același antet ca `printTable`, ca hârtiile clinicii să
 * arate a fi ale aceleiași clinici. Corpul e HTML gata construit de apelant —
 * el știe ce câmpuri are documentul lui — iar semnăturile sunt aici fiindcă
 * fiecare document tipărit se semnează, și linia de semnătură desenată de
 * mână iese de fiecare dată altfel.
 */
export function printDoc(opts: {
  title: string;
  clinicName: string;
  meta?: string;
  /** HTML deja escapat de apelant. */
  body: string;
  /** Câte o linie de semnătură pentru fiecare nume dat. */
  signatures?: string[];
}): boolean {
  const brand = printBrandColor();
  const signs = (opts.signatures ?? [])
    .map((s) => `<div class="sign"><div class="line"></div>${esc(s)}</div>`)
    .join('');
  const w = openPrintWindow(
    `<!doctype html><html><head><meta charset="utf-8"><title>${esc(opts.title)}</title>
    <style>@page{margin:16mm}body{font-family:Arial,sans-serif;color:#0a0f1e;font-size:12.5px;line-height:1.6}
    .head{display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid ${brand};padding-bottom:8px;margin-bottom:6px}
    .brand{font-size:20px;font-weight:800;color:${brand}}.clinic{font-size:11px;color:#666}
    .meta{font-size:11px;color:#666;margin-bottom:16px}
    h1{font-size:16px;margin:0 0 14px;text-align:center;text-transform:uppercase;letter-spacing:.04em}
    dl{display:grid;grid-template-columns:auto 1fr;gap:4px 14px;margin:0 0 14px}
    dt{color:#555}dd{margin:0;font-weight:700}
    p{margin:0 0 10px;text-align:justify}
    p.center{text-align:center;color:#555;font-size:11.5px;margin-top:-8px}
    table{width:100%;border-collapse:collapse;font-size:11.5px;margin:10px 0 14px}
    th,td{border:1px solid #d7dbe3;padding:5px 7px;text-align:left}
    th{background:#f3f5f9;font-size:10.5px;text-transform:uppercase;letter-spacing:.03em}
    tbody th,tfoot th{background:transparent;text-transform:none;letter-spacing:0;font-size:11.5px}
    .num{text-align:right;font-variant-numeric:tabular-nums}
    tfoot td,tfoot th{font-weight:800;border-top:2px solid #0a0f1e}
    .fill{border-bottom:1px solid #0a0f1e;min-height:20px;margin:2px 0 10px}
    .protocol{white-space:pre-wrap;font-size:12.5px;line-height:1.7}
    .signs{display:flex;gap:48px;margin-top:34px}
    .sign{flex:1;font-size:11.5px}
    .line{border-bottom:1px solid #0a0f1e;height:32px;margin-bottom:4px}
    @media print{.noprint{display:none}tr{break-inside:avoid}}</style></head>
    <body><div class="head"><div class="brand">${esc(opts.clinicName)}</div><div class="clinic">${esc(opts.title)}</div></div>
    <div class="meta">${esc(opts.meta ?? '')}</div>
    ${opts.body}
    ${signs ? `<div class="signs">${signs}</div>` : ''}
    <div class="noprint" style="margin-top:22px;text-align:center"><button data-print style="padding:10px 22px;font-size:14px;cursor:pointer">Printează / Salvează PDF</button></div>
    </body></html>`,
    { width: 900, height: 1000 },
  );
  return w !== null;
}

/**
 * Un document care își aduce propriul stil: formularele oficiale.
 *
 * `printDoc` impune antetul clinicii și marginile ei, ceea ce e exact ce
 * trebuie pentru hârtiile clinicii — și exact ce nu trebuie pentru un
 * formular aprobat prin ordin de minister, care are antetul lui, forma lui și
 * ordinea lui de rubrici. Aici trece documentul întreg, cu `<style>`-ul lui.
 *
 * Fereastra se deschide, scrie și cheamă tipărirea; dacă browserul a blocat
 * ferestrele, întoarce `false`, iar apelantul spune de ce n-a apărut nimic.
 *
 * `head` și `body` intră separat fiindcă antetul documentului îl scrie
 * funcția — titlul și codificarea sunt aceleași pentru orice hârtie.
 */
export function printRawDoc(title: string, head: string, body: string): boolean {
  const w = openPrintWindow(
    `<!doctype html><html lang="ro"><head><meta charset="utf-8"><title>${esc(title)}</title>${head}</head><body>${body}</body></html>`,
    { width: 900, height: 1100, delay: 500 },
  );
  return w !== null;
}

/** Escapare pentru corpurile construite de apelanți. */
export const escapeHtml = esc;
